/*
    This file uses code from @react-three/drei.
    Original Copyright (c) 2020 react-spring
    Source: https://github.com/pmndrs/drei
    License: MIT (see Frontend/src/THIRD-PARTY-LICENSES/drei-MIT.txt)
*/

import { CameraControls, Hud, OrthographicCamera } from '@react-three/drei';
import { useThree, useFrame, invalidate } from '@react-three/fiber';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';
import { Vector3, Object3D, Matrix4, Quaternion, type Group, type OrthographicCamera as OrthographicCameraType } from 'three';

interface CustomGizmoHelperProps {
    alignment?:
        | 'top-left'
        | 'top-right'
        | 'bottom-right'
        | 'bottom-left'
        | 'bottom-center'
        | 'center-right'
        | 'center-left'
        | 'center-center'
        | 'top-center';
    margin?: [number, number];
    renderPriority?: number;
    scale?: number;
    children: ReactNode;
    onUpdate?: () => void;
    onTarget?: () => Vector3;
}

const turnRate = 2 * Math.PI; // turn rate in angles per second
const dummy = new Object3D();
const matrix = new Matrix4();
const [q1, q2] = [new Quaternion(), new Quaternion()];
const target = new Vector3();
const targetPosition = new Vector3();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isCameraControls = (controls: any): controls is CameraControls => {
    return controls && 'getTarget' in controls;
};

export const CustomGizmoHelper = ({
    alignment = 'top-left',
    margin = [80, 80],
    renderPriority = 2,
    scale = 1,
    children,
    onUpdate,
    onTarget
}: CustomGizmoHelperProps) => {
    const size = useThree((state) => state.size);

    const mainCamera = useThree((state) => state.camera);
    const defaultControls = useThree((state) => state.controls);

    const gizmoRef = useRef<Group | null>(null);
    const gizmoCameraRef = useRef<OrthographicCameraType | null>(null);

    const animating = useRef<boolean>(false);
    const radius = useRef<number>(0);
    const focusPoint = useRef<Vector3>(new Vector3(0, 0, 0));

    const tweenCamera = useCallback(
        (direction: Vector3, up?: Vector3) => {
            animating.current = true;

            if (onTarget) {
                onTarget();
            } else if (defaultControls && isCameraControls(defaultControls)) {
                defaultControls.getTarget(focusPoint.current);
            }

            radius.current = mainCamera.position.distanceTo(target);
            q1.copy(mainCamera.quaternion);

            targetPosition.copy(direction).multiplyScalar(radius.current).add(target);

            dummy.up.copy(up ?? mainCamera.up).normalize();
            dummy.lookAt(targetPosition);
            q2.copy(dummy.quaternion);

            invalidate();
        },
        [defaultControls, mainCamera, onTarget]
    );

    useFrame((_, delta) => {
        if (!gizmoCameraRef.current || !gizmoRef.current) {
            return;
        }

        if (animating.current) {
            if (q1.angleTo(q2) < 0.0001) {
                animating.current = false;
            } else {
                if (!mainCamera) {
                    return;
                }

                const step = delta * turnRate;
                q1.rotateTowards(q2, step);

                mainCamera.position
                    .set(0, 0, 1)
                    .applyQuaternion(q1)
                    .multiplyScalar(radius.current)
                    .add(focusPoint.current);

                mainCamera.quaternion.copy(q1);

                if (!defaultControls || !isCameraControls(defaultControls)) {
                    return;
                }

                defaultControls.setPosition(mainCamera.position.x, mainCamera.position.y, mainCamera.position.z);

                if (onUpdate) {
                    onUpdate();
                } else {
                    defaultControls.update(delta);
                }

                invalidate();
            }
        }

        if (mainCamera && gizmoRef.current) {
            matrix.copy(mainCamera.matrix).invert();
            gizmoRef.current.quaternion.setFromRotationMatrix(matrix);
        }
    });

    const gizmoHelperContext = useMemo(() => ({ tweenCamera }), [tweenCamera]);

    let x = 0;
    let y = 0;
    if (margin) {
        x = alignment?.endsWith('-center')
            ? 0
            : alignment?.endsWith('-left')
              ? -size.width / 2 + margin[0]
              : size.width / 2 - margin[0];

        y = alignment?.startsWith('center-')
            ? 0
            : alignment?.startsWith('top-')
              ? size.height / 2 - margin[0]
              : -size.height / 2 + margin[0];
    }

    return (
        <Hud renderPriority={renderPriority}>
            <GizmoHelperContext.Provider value={gizmoHelperContext}>
                <OrthographicCamera ref={gizmoCameraRef} position={[0, 0, 200]} makeDefault />
                <group ref={gizmoRef} position={[x, y, 0]} scale={scale}>
                    {children}
                </group>
            </GizmoHelperContext.Provider>
        </Hud>
    );
};

interface CustomGizmoHelperContext {
    tweenCamera: (direction: Vector3, up?: Vector3) => void;
}

const GizmoHelperContext = createContext<CustomGizmoHelperContext | undefined>(undefined);

export const useCustomGizmoHelperContext = () => {
    const ctx = useContext(GizmoHelperContext);
    if (!ctx) {
        throw new Error('useCustomGizmoHelperContext must be used inside <CustomGizmoHelper>');
    }
    return ctx;
};
